/**
 * Componente principal de Galeria de Mídia
 * 
 * Dialog completo para gerenciamento de arquivos de mídia do tenant:
 * - Grid de mídia com cards de preview
 * - Filtros por tipo (imagens, vídeos, áudios, documentos)
 * - Barra de uso de armazenamento
 * - Zona de drag & drop para upload
 * - Busca por nome de arquivo
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Search, Upload, Image, Video, Music, FileText } from 'lucide-react';
import { MediaGalleryDialogProps, MediaFileType } from '@/types/media';
import { useMediaGallery } from '@/hooks/useMediaGallery';
import { MediaUploadZone } from './MediaUploadZone';
import { MediaUsageBar } from './MediaUsageBar';
import { MediaPreviewCard } from './MediaPreviewCard';
import { createLogger } from '@/lib/logger';

const logger = createLogger('MediaGalleryDialog');

export function MediaGalleryDialog({
  open,
  onOpenChange,
  selectionMode = false,
  onSelect,
}: MediaGalleryDialogProps) {
  const [selectedType, setSelectedType] = useState<MediaFileType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMediaId, setSelectedMediaId] = useState<string | undefined>();

  const {
    mediaList,
    usage,
    isLoadingMedia,
    isLoadingUsage,
    isUploading,
    isDeleting,
    uploadMedia,
    deleteMedia,
    getMediaUrl,
  } = useMediaGallery({
    file_type: selectedType,
    search: searchQuery,
  });

  // =====================================================
  // HANDLERS
  // =====================================================

  const handleUpload = (file: File) => {
    logger.debug('Iniciando upload', { fileName: file.name });
    uploadMedia(file);
  };

  const handleDelete = (mediaId: string) => {
    logger.debug('Deletando mídia', { mediaId });
    deleteMedia(mediaId);
  };

  const handleSelect = (media: any) => {
    if (selectionMode && onSelect) {
      logger.debug('Mídia selecionada', { mediaId: media.id });
      onSelect(media);
      onOpenChange(false);
    }
  };

  // Filtrar lista baseado na busca
  const filteredMedia = mediaList.filter((media) =>
    media.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // =====================================================
  // RENDERIZAÇÃO
  // =====================================================

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[80vh] flex flex-col p-0">
        {/* Header compacto: título + armazenamento na mesma linha */}
        <DialogHeader className="px-6 pt-4 pb-2">
          <div className="flex items-start justify-between gap-4 pr-6">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Galeria de Mídia
              </DialogTitle>
              <DialogDescription className="mt-1">
                {selectionMode
                  ? 'Selecione um arquivo da sua galeria'
                  : 'Gerencie seus arquivos de mídia para uso nos fluxos'}
              </DialogDescription>
            </div>
            {/* Resumo compacto de armazenamento */}
            {usage && (
              <MediaUsageBar
                totalBytes={usage.total_bytes}
                totalFiles={usage.total_files}
                variant="compact"
                className="mt-1"
              />
            )}
          </div>
        </DialogHeader>

        {/* Abas de filtro por tipo */}
        <Tabs
          value={selectedType}
          onValueChange={(value) => setSelectedType(value as MediaFileType | 'all')}
          className="flex-1 flex flex-col overflow-hidden"
        >
          <div className="px-6 pb-2 space-y-2">
            <TabsList className="grid grid-cols-5 w-full">
              <TabsTrigger value="all" className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                <span className="hidden sm:inline">Todos</span>
                <span className="text-xs text-muted-foreground">
                  ({mediaList.length})
                </span>
              </TabsTrigger>
              <TabsTrigger value="image" className="flex items-center gap-2">
                <Image className="h-4 w-4" />
                <span className="hidden sm:inline">Imagens</span>
                <span className="text-xs text-muted-foreground">
                  ({usage?.by_type.image.count || 0})
                </span>
              </TabsTrigger>
              <TabsTrigger value="video" className="flex items-center gap-2">
                <Video className="h-4 w-4" />
                <span className="hidden sm:inline">Vídeos</span>
                <span className="text-xs text-muted-foreground">
                  ({usage?.by_type.video.count || 0})
                </span>
              </TabsTrigger>
              <TabsTrigger value="audio" className="flex items-center gap-2">
                <Music className="h-4 w-4" />
                <span className="hidden sm:inline">Áudios</span>
                <span className="text-xs text-muted-foreground">
                  ({usage?.by_type.audio.count || 0})
                </span>
              </TabsTrigger>
              <TabsTrigger value="document" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span className="hidden sm:inline">Docs</span>
                <span className="text-xs text-muted-foreground">
                  ({usage?.by_type.document.count || 0})
                </span>
              </TabsTrigger>
            </TabsList>

            {/* Campo de busca */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome do arquivo..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <TabsContent value={selectedType} className="flex-1 overflow-hidden m-0">
            <ScrollArea className="h-full px-6">
              {/* Zona de upload */}
              <MediaUploadZone
                onUpload={handleUpload}
                isUploading={isUploading}
                disabled={isUploading || isDeleting}
                className="mb-6"
              />

              {/* Loading state */}
              {isLoadingMedia && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              )}

              {/* Empty state */}
              {!isLoadingMedia && filteredMedia.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <p className="text-sm">
                    {searchQuery
                      ? 'Nenhum arquivo encontrado'
                      : 'Nenhum arquivo na galeria'}
                  </p>
                  <p className="text-xs mt-2">
                    {!searchQuery && 'Faça upload de arquivos para começar'}
                  </p>
                </div>
              )}

              {/* Grid de mídia */}
              {!isLoadingMedia && filteredMedia.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 pb-6">
                  {filteredMedia.map((media) => (
                    <MediaPreviewCard
                      key={media.id}
                      media={media}
                      mediaUrl={getMediaUrl(media.storage_path)}
                      onDelete={selectionMode ? undefined : handleDelete}
                      onSelect={selectionMode ? handleSelect : undefined}
                      isSelected={selectedMediaId === media.id}
                      selectionMode={selectionMode}
                    />
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
